/**
 * EmptyCanvas — the blankest possible H2000 map.
 *
 * One ground platform (so the player has somewhere to land), one spawn point
 * above it, and nothing else. No entities, no triggers, no parallax, no
 * spawn rules, no auto-generated tile data. Designed as a fresh slate to
 * paint into via the in-game editor.
 *
 * Usage:
 *   make editor      → boots dev-fast-memory and opens ?preset=editor which
 *                       defaults to this map (per PRESET_DEFAULT_MAPS in
 *                       devConfig.ts).
 *
 * Alternatively, load the map directly from another preset by switching to
 * 'empty-canvas' via the in-game map picker (Map Tools panel).
 *
 * Naming convention: matches the tile-coord conventions documented in
 * docs/guides/LEVEL_FORMATS_EXPLAINED.md. Spawn points are tile coordinates;
 * defaultSpawn is pixel coordinates (multiply by tile size).
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = 'empty-canvas';
const MAP_NAME = 'Empty Canvas';
const VERSION = '1.0';

const W = MAP_DEFAULTS.widthTiles;
const H = MAP_DEFAULTS.heightTiles;
const T = MAP_DEFAULTS.tileSize;

// All 5 editor layers exist but are empty — ready for the editor to paint into
// without re-creating the layer structure.
const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

// Player spawn — uses MAP_DEFAULTS for consistency with StarterMap. Player is
// 40×40 so spawning at MAP_DEFAULTS.spawnPoint puts the feet on the ground
// platform. Don't generalize the -40 offset to any other entity type — see
// docs/guides/LEVEL_FORMATS_EXPLAINED.md (Coordinate Gotcha).
const spawnPoints = [
  { x: MAP_DEFAULTS.spawnPoint.x, y: MAP_DEFAULTS.spawnPoint.y },
];
const defaultSpawn = {
  x: MAP_DEFAULTS.spawnPoint.x * T,
  y: MAP_DEFAULTS.spawnPoint.y * T,
};

// One ground platform spanning the full canvas. Without it the player would
// fall forever the moment the map loads — that's the only reason this isn't
// truly "empty." Everything else can be added/removed in the editor.
const runtimePlatforms = [
  {
    id: 'ground-floor',
    x: 0,
    y: MAP_DEFAULTS.ground.y,
    width: W * T,
    height: MAP_DEFAULTS.ground.height,
    type: MAP_DEFAULTS.ground.type,
    color: MAP_DEFAULTS.ground.color,
  },
];

export const EmptyCanvas: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: MAP_DEFAULTS.backgroundColor,
  gravity: MAP_DEFAULTS.gravity,
  spawnPoints,
  defaultSpawn,
  layers: [
    { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities: [] },
    { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],
  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'town',
  metadata: {
    author: 'DJ',
    description: 'Blank slate. One ground platform, one spawn point, nothing else. Loaded by ?preset=editor.',
    tags: ['template', 'blank', 'editor'],
  },
};
